"use client";

import { useState, type FormEvent, type CSSProperties, type ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { products as catalog } from "@/lib/content/products";
import { isRealPhoto } from "@/lib/brand-mapping";

/* ─── BP Derma Color System ─── */
const BP_COLORS = {
  yellow:    { hex: "#FFD700", bg: "bg-yellow-50",  border: "border-yellow-300", text: "text-yellow-700", label: "Oil & Acne Control" },
  orange:    { hex: "#FFA500", bg: "bg-orange-50",  border: "border-orange-300", text: "text-orange-700", label: "Antioxidant Defense" },
  pink:      { hex: "#FFB6C1", bg: "bg-pink-50",    border: "border-pink-300",   text: "text-pink-700",   label: "Age Defense" },
  violet:    { hex: "#EE82EE", bg: "bg-violet-50",  border: "border-violet-300", text: "text-violet-700", label: "Hydration & Nourish" },
  lightBlue: { hex: "#ADD8E6", bg: "bg-sky-50",     border: "border-sky-300",    text: "text-sky-700",    label: "Sensitive Care" },
  gray:      { hex: "#808080", bg: "bg-gray-50",    border: "border-gray-300",   text: "text-gray-700",   label: "Bright & Even Tone" },
  green:     { hex: "#90EE90", bg: "bg-emerald-50", border: "border-emerald-300",text: "text-emerald-700",label: "Eye Care" },
  deepOrange:{ hex: "#FF8C00", bg: "bg-amber-50",   border: "border-amber-300",  text: "text-amber-700",  label: "Solar Shield" },
} as const;

type ColorKey = keyof typeof BP_COLORS;

/* ─── Product SKU map for thumbnails — sourced from the live catalog so it
   stays in sync with new product photography. SKUs without a real shoot photo
   resolve to undefined and the UI falls back to a placeholder. ─── */
const PRODUCT_IMAGES: Record<string, string | undefined> = Object.fromEntries(
  catalog.map((p) => [
    p.sku,
    p.image && isRealPhoto(p.image) ? p.image : undefined,
  ])
);

/* ─── Quiz Questions ─── */
const QUESTIONS = [
  {
    id: "age",
    question: "How old are you?",
    subtitle: "This helps us match the right active strengths for your skin.",
    options: [
      { value: "20", label: "Under 25", desc: "Prevention & balance" },
      { value: "30", label: "25–34",    desc: "Early protection" },
      { value: "40", label: "35–44",    desc: "Active renewal" },
      { value: "50", label: "45+",      desc: "Intensive repair" },
    ],
  },
  {
    id: "skintone",
    question: "What best describes your skin tone?",
    subtitle: "Skin tone affects sun sensitivity, pigmentation risk, and active ingredient selection.",
    options: [
      { value: "fair",     label: "Fair / Light",       desc: "Burns easily, rarely tans" },
      { value: "medium",   label: "Medium / Olive",     desc: "Sometimes burns, tans gradually" },
      { value: "tan",      label: "Tan / Brown",        desc: "Rarely burns, tans easily" },
      { value: "deep",     label: "Deep / Dark",        desc: "Almost never burns, rich melanin" },
    ],
  },
  {
    id: "climate",
    question: "Where do you live?",
    subtitle: "Climate and environment shape what your skin needs every day.",
    options: [
      { value: "hot-humid",   label: "Hot & humid",     desc: "Tropical, coastal, or monsoon climates" },
      { value: "hot-dry",     label: "Hot & dry",        desc: "Desert, arid, or Mediterranean summers" },
      { value: "temperate",   label: "Temperate / mild", desc: "Moderate seasons, Central Europe, coastal" },
      { value: "cold",        label: "Cold / harsh",     desc: "Long winters, dry heated air indoors" },
    ],
  },
  {
    id: "oiliness",
    question: "How does your skin feel by midday?",
    subtitle: "Think about 3–4 hours after cleansing, no products applied.",
    options: [
      { value: "oily",   label: "Shiny & oily",      desc: "Visible shine on forehead, nose, chin" },
      { value: "combo",  label: "Mixed — oily T-zone", desc: "Oily center, normal or dry cheeks" },
      { value: "normal", label: "Comfortable",         desc: "Neither tight nor shiny" },
      { value: "dry",    label: "Tight & dry",         desc: "Feels rough, flaky, or pulling" },
    ],
  },
  {
    id: "sensitivity",
    question: "How does your skin react to new products?",
    subtitle: "Think about redness, stinging, or irritation.",
    options: [
      { value: "very",     label: "Very reactive",   desc: "Often red, stinging, or itchy" },
      { value: "somewhat", label: "Somewhat sensitive", desc: "Occasional irritation with certain products" },
      { value: "resilient",label: "Resilient",        desc: "Rarely reacts, handles most products well" },
    ],
  },
  {
    id: "concerns",
    question: "What are your top skin concerns?",
    subtitle: "Select all that apply — we'll prioritize your routine.",
    multi: true,
    options: [
      { value: "acne",         label: "Breakouts & blemishes",  desc: "Pimples, blackheads, clogged pores" },
      { value: "aging",        label: "Fine lines & wrinkles",  desc: "Loss of firmness, crow's feet" },
      { value: "pigmentation", label: "Dark spots & uneven tone", desc: "Sun spots, melasma, post-acne marks" },
      { value: "dryness",      label: "Dehydration & flaking",  desc: "Rough texture, tightness" },
      { value: "dullness",     label: "Dull & tired skin",      desc: "Lack of glow, uneven texture" },
      { value: "eye",          label: "Dark circles & puffiness", desc: "Under-eye concerns" },
      { value: "sun",          label: "Sun damage & protection", desc: "UV sensitivity, prevention" },
    ],
  },
  {
    id: "lifestyle",
    question: "What best describes your daily lifestyle?",
    subtitle: "Your daily routine affects what your skin is exposed to.",
    options: [
      { value: "urban",   label: "City / office", desc: "Air conditioning, screens, pollution exposure" },
      { value: "outdoor", label: "Active / outdoors", desc: "Regular sun, wind, and weather exposure" },
      { value: "mixed",   label: "Mixed",          desc: "A bit of both, varies day to day" },
    ],
  },
];

/* ─── Product recommendations by SKU ─── */
interface ProductRec {
  sku: string;
  name: string;
  step: string;
  category: string;
  color: ColorKey;
}

const PRODUCTS: Record<string, ProductRec> = {
  "1001": { sku: "1001", name: "Fresh Calm Cleansing Gel",             step: "Cleanse",        category: "sensitive",    color: "lightBlue" },
  "1002": { sku: "1002", name: "Bright Glow Cleansing Foam",           step: "Cleanse",        category: "sensitive",    color: "orange" },
  "1003": { sku: "1003", name: "Deep Clear Cleansing Face Foam",       step: "Cleanse",        category: "oily-acne",    color: "yellow" },
  "1006": { sku: "1006", name: "80% GFF & Seaweed Tone Essence",       step: "Tone & Balance", category: "normal-dry",   color: "violet" },
  "1007": { sku: "1007", name: "10% GFF & BHA Balance Mist",           step: "Tone & Balance", category: "oily-acne",    color: "yellow" },
  "1015": { sku: "1015", name: "5% Vitamin C Blemish Serum",           step: "Focus & Treat",  category: "oily-acne",    color: "yellow" },
  "1020": { sku: "1020", name: "0.1% Retinol Glow Skin Milk",          step: "Focus & Treat",  category: "age-defense",  color: "pink" },
  "1021": { sku: "1021", name: "0.3% Retinol + Bakuchiol Glow Milk",   step: "Focus & Treat",  category: "age-defense",  color: "pink" },
  "1023": { sku: "1023", name: "3% Peptide + HA Lifting Serum",        step: "Focus & Treat",  category: "age-defense",  color: "pink" },
  "1029": { sku: "1029", name: "10% Vitamin C AOX Defense Serum",      step: "Focus & Treat",  category: "antioxidant",  color: "orange" },
  "1030": { sku: "1030", name: "15% Vitamin C AOX+ Defense Serum",     step: "Focus & Treat",  category: "antioxidant",  color: "orange" },
  "1031": { sku: "1031", name: "15% Vitamin C Collagen Boost Cream",   step: "Boost & Hydrate",category: "antioxidant",  color: "orange" },
  "1032": { sku: "1032", name: "Amino Acids Comfort Hydration Gel",    step: "Boost & Hydrate",category: "oily-acne",    color: "yellow" },
  "1033": { sku: "1033", name: "Amino Acids Nourish & Hydrate Cream",  step: "Boost & Hydrate",category: "normal-dry",   color: "violet" },
  "1035": { sku: "1035", name: "Peptide Eye Cream",                    step: "Focus & Treat",  category: "eye-care",     color: "green" },
  "1036": { sku: "1036", name: "Niacinamide Eye Serum",                step: "Focus & Treat",  category: "eye-care",     color: "green" },
  "1040": { sku: "1040", name: "Alpha-Arbutin Brightening Serum",      step: "Focus & Treat",  category: "brightening",  color: "gray" },
  "1041": { sku: "1041", name: "Alpha-Arbutin Brightening Cream SPF30",step: "Boost & Hydrate",category: "brightening",  color: "gray" },
  "1082": { sku: "1082", name: "Sun Protection Cream SPF 50+",         step: "Protect",        category: "solar-shield", color: "deepOrange" },
};

/* ─── Recommendation engine ─── */
type Answers = Record<string, string | string[]>;

interface Routine {
  am: ProductRec[];
  pm: ProductRec[];
  colors: ColorKey[];
  profile: string;
  summary: string;
}

function generateRoutine(answers: Answers): Routine {
  const age = answers.age as string;
  const tone = (answers.skintone || "medium") as string;
  const climate = (answers.climate || "temperate") as string;
  const oil = answers.oiliness as string;
  const sens = answers.sensitivity as string;
  const concerns = (answers.concerns || []) as string[];
  const lifestyle = (answers.lifestyle || "mixed") as string;

  const isOily = oil === "oily" || oil === "combo";
  const isDry = oil === "dry";
  const isSensitive = sens === "very" || sens === "somewhat";
  const isVSens = sens === "very";
  const matureAge = age === "40" || age === "50";
  const hasAcne = concerns.includes("acne");
  const hasAging = concerns.includes("aging");
  const hasPigment = concerns.includes("pigmentation");
  const hasDryness = concerns.includes("dryness");
  const hasDullness = concerns.includes("dullness");
  const hasEye = concerns.includes("eye");
  const hasSun = concerns.includes("sun");

  const isFair = tone === "fair";
  const isDeep = tone === "deep";
  const pigmentRisk = tone === "medium" || tone === "tan" || tone === "deep";

  const isHumid = climate === "hot-humid";
  const isDryClimate = climate === "hot-dry" || climate === "cold";
  const isHighUV = climate === "hot-humid" || climate === "hot-dry";
  const isCold = climate === "cold";

  const am: ProductRec[] = [];
  const pm: ProductRec[] = [];
  const colors: ColorKey[] = [];

  // --- PROFILE NAME ---
  let profile = "";
  if (isOily && hasAcne) profile = "Clear & Balanced";
  else if ((isDry || isDryClimate || isCold) && hasDryness) profile = "Deep Nourish";
  else if (hasAging && matureAge) profile = "Age Renew";
  else if (isSensitive) profile = "Calm & Gentle";
  else if (hasPigment || (pigmentRisk && isHighUV)) profile = "Bright & Even";
  else if (hasDullness) profile = "Radiance Boost";
  else profile = "Balanced Glow";

  // --- AM ROUTINE ---
  if ((isOily && hasAcne) || (isOily && isHumid)) { am.push(PRODUCTS["1003"]); colors.push("yellow"); }
  else if (isVSens)      { am.push(PRODUCTS["1001"]); colors.push("lightBlue"); }
  else                   { am.push(PRODUCTS["1002"]); }

  if (isOily || isHumid)  { am.push(PRODUCTS["1007"]); }
  else                    { am.push(PRODUCTS["1006"]); colors.push("violet"); }

  if (isSensitive || (isFair && isHighUV)) { am.push(PRODUCTS["1029"]); colors.push("orange"); }
  else                                      { am.push(PRODUCTS["1030"]); colors.push("orange"); }

  if (pigmentRisk && isHighUV && !hasPigment) {
    am.push(PRODUCTS["1040"]); colors.push("gray");
  }

  if (hasEye) {
    if (isSensitive) { am.push(PRODUCTS["1036"]); colors.push("green"); }
    else             { am.push(PRODUCTS["1035"]); colors.push("green"); }
  }

  if (isOily && !isDryClimate) { am.push(PRODUCTS["1032"]); }
  else if (matureAge || hasDryness || isDryClimate || isCold) { am.push(PRODUCTS["1031"]); }
  else        { am.push(PRODUCTS["1033"]); }

  am.push(PRODUCTS["1082"]);
  colors.push("deepOrange");

  // --- PM ROUTINE ---
  if ((isOily && hasAcne) || (isOily && isHumid)) { pm.push(PRODUCTS["1003"]); }
  else if (isVSens)      { pm.push(PRODUCTS["1001"]); }
  else                   { pm.push(PRODUCTS["1002"]); }

  if (isOily || isHumid) { pm.push(PRODUCTS["1007"]); }
  else                   { pm.push(PRODUCTS["1006"]); }

  if (hasPigment || (pigmentRisk && hasDullness)) {
    if (isSensitive) { pm.push(PRODUCTS["1041"]); colors.push("gray"); }
    else             { pm.push(PRODUCTS["1040"]); colors.push("gray"); }
  } else if (hasAging && matureAge) {
    if (isSensitive || isFair) { pm.push(PRODUCTS["1020"]); colors.push("pink"); }
    else                        { pm.push(PRODUCTS["1021"]); colors.push("pink"); }
    pm.push(PRODUCTS["1023"]);
  } else if (hasAcne && isOily) {
    pm.push(PRODUCTS["1015"]);
  } else if (hasDullness) {
    pm.push(PRODUCTS["1029"]);
  } else if (hasAging) {
    pm.push(PRODUCTS["1020"]); colors.push("pink");
  }

  if (hasEye) {
    if (!am.find(p => p.sku === "1035" || p.sku === "1036")) colors.push("green");
    if (isSensitive) { pm.push(PRODUCTS["1036"]); }
    else             { pm.push(PRODUCTS["1035"]); }
  }

  if ((hasAging && !isSensitive && matureAge) || (isDryClimate && !isOily)) {
    pm.push(PRODUCTS["1031"]);
  }

  if (isOily && !isDryClimate) { pm.push(PRODUCTS["1032"]); }
  else                          { pm.push(PRODUCTS["1033"]); }

  const uniqueColors = [...new Set(colors)];

  const parts: string[] = [];
  if (isOily) parts.push("oil control");
  if (isDry || isDryClimate) parts.push("deep hydration");
  if (isSensitive) parts.push("gentle actives");
  if (hasAging) parts.push("anti-aging renewal");
  if (hasPigment || pigmentRisk) parts.push("brightening & tone correction");
  if (hasEye) parts.push("eye care");
  parts.push("daily SPF protection");
  if (lifestyle === "outdoor" || hasSun || isHighUV || isFair) parts.push("enhanced UV defense");
  if (isDryClimate || isCold) parts.push("barrier repair");
  if (isHumid) parts.push("lightweight textures");

  const toneNote = isFair ? "fair skin (higher UV sensitivity)"
    : isDeep ? "rich skin tone (melanin-aware actives)"
    : pigmentRisk ? "medium-to-tan tone (pigmentation-aware)"
    : "";

  const climateName = climate === "hot-humid" ? "hot & humid climate"
    : climate === "hot-dry" ? "hot & dry climate"
    : climate === "cold" ? "cold climate"
    : "temperate climate";

  const summary = `Your routine focuses on ${parts.join(", ")}. Tailored for ${toneNote ? toneNote + " in a " : "a "}${climateName}.`;

  return { am, pm, colors: uniqueColors, profile, summary };
}

/* ─── Quiz Modes ─── */
const SHORT_IDS = ["age", "oiliness", "sensitivity", "concerns"];
const EXTENDED_IDS = QUESTIONS.map(q => q.id);

/* ─── Routine Step Row (brand) ─── */
function RoutineStepRow({
  product,
  index,
  locale,
}: {
  product: ProductRec;
  index: number;
  locale: string;
}) {
  const imgSrc = PRODUCT_IMAGES[product.sku];

  return (
    <Link
      href={`/${locale}/products/${product.category}/${product.sku}`}
      style={{
        display: "grid",
        gridTemplateColumns: "32px 56px 1fr auto",
        gap: 14,
        alignItems: "center",
        padding: "14px 16px",
        background: "var(--bp-bg)",
        borderTop: "1px solid var(--bp-rule)",
        color: "var(--bp-ink)",
        textDecoration: "none",
      }}
    >
      <span className="bp-mono" style={{ color: "var(--bp-ink)" }}>
        {String(index + 1).padStart(2, "0")}
      </span>

      {imgSrc ? (
        <div
          style={{
            width: 56,
            height: 56,
            background: "var(--bp-bg-2)",
            border: "1px solid var(--bp-rule)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Image
            src={imgSrc}
            alt={product.name}
            width={56}
            height={56}
            style={{ objectFit: "cover", width: "100%", height: "100%" }}
          />
        </div>
      ) : (
        <div
          style={{
            width: 56,
            height: 56,
            background: "var(--bp-bg-2)",
            border: "1px solid var(--bp-rule)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--bp-mono)",
            fontSize: 10,
            color: "var(--bp-ink-3)",
            letterSpacing: "0.06em",
          }}
        >
          {product.sku}
        </div>
      )}

      <div style={{ minWidth: 0 }}>
        <div className="bp-mono-sm">{product.step.toUpperCase()}</div>
        <div
          style={{
            fontFamily: "var(--bp-serif)",
            fontSize: 18,
            fontWeight: 300,
            letterSpacing: "-0.01em",
            color: "var(--bp-ink)",
            marginTop: 4,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {product.name}
        </div>
      </div>
      <span className="bp-mono-sm" style={{ color: "var(--bp-ink-3)" }}>↗</span>
    </Link>
  );
}

/* ─── Component ─── */
interface SkinQuizProps {
  locale: string;
  dict: Record<string, string>;
}

export default function SkinQuiz({ locale, dict }: SkinQuizProps) {
  const [mode, setMode] = useState<"choose" | "short" | "extended">("choose");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [result, setResult] = useState<Routine | null>(null);
  const [saved, setSaved] = useState(false);
  const [shared, setShared] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadError, setLeadError] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [instagram, setInstagram] = useState("");

  const activeIds = mode === "short" ? SHORT_IDS : EXTENDED_IDS;
  const activeQuestions = QUESTIONS.filter(q => activeIds.includes(q.id));
  const q = activeQuestions[step];
  const isMulti = q ? ("multi" in q && q.multi) : false;
  const totalSteps = activeQuestions.length;
  const progress = ((step + 1) / totalSteps) * 100;

  function selectOption(value: string) {
    if (isMulti) {
      const current = (answers[q.id] || []) as string[];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      setAnswers({ ...answers, [q.id]: next });
    } else {
      const next = { ...answers, [q.id]: value };
      setAnswers(next);
      if (step < totalSteps - 1) {
        setTimeout(() => setStep(step + 1), 300);
      } else {
        setTimeout(() => setResult(generateRoutine(next)), 300);
      }
    }
  }

  function nextMulti() {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      setResult(generateRoutine(answers));
    }
  }

  function restart() {
    setMode("choose");
    setStep(0);
    setAnswers({});
    setResult(null);
    setSaved(false);
    setShared(false);
    setLeadDone(false);
    setLeadSubmitting(false);
    setLeadError("");
    setEmail("");
    setPhone("");
    setInstagram("");
  }

  function handleSaveRoutine(r: Routine) {
    try {
      const key = "bpderma_quiz_result";
      const payload = {
        profile: r.profile,
        summary: r.summary,
        amSkus: r.am.map(p => p.sku),
        pmSkus: r.pm.map(p => p.sku),
        savedAt: new Date().toISOString(),
        answers,
      };
      localStorage.setItem(key, JSON.stringify(payload));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      /* localStorage not available */
    }
  }

  function handleShare(r: Routine) {
    const text = `My BP Derma skin profile: ${r.profile}\n\n${r.summary}\n\nFind yours at bpderma.com/routines`;
    if (navigator.share) {
      navigator.share({
        title: `My Skin Profile: ${r.profile}`,
        text,
        url: `${window.location.origin}/${locale}/routines`,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => {
        setShared(true);
        setTimeout(() => setShared(false), 2500);
      }).catch(() => {
        window.prompt("Copy your skin profile:", text);
      });
    }
  }

  async function submitLead(e: FormEvent) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setLeadError(dict.quizEmailError ?? "Please enter a valid email address.");
      return;
    }
    if (!result) return;
    setLeadError("");
    setLeadSubmitting(true);
    try {
      await fetch("/api/quiz-leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          phone: phone.trim(),
          instagram: instagram.trim(),
          locale,
          profile: result.profile,
          summary: result.summary,
          colors: result.colors,
          amSkus: result.am.map((p) => p.sku),
          pmSkus: result.pm.map((p) => p.sku),
          answers,
        }),
      });
    } catch {
      /* best-effort: never block the user's result on a network hiccup */
    }
    setLeadSubmitting(false);
    setLeadDone(true);
  }

  // ─── MODE SELECTION ───
  if (mode === "choose") {
    return (
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <span className="bp-mono">
            <span className="bp-bracket">{dict.chooseQuiz ?? "CHOOSE YOUR QUIZ"}</span>
          </span>
          <p
            style={{
              marginTop: 12,
              fontSize: 15,
              color: "var(--bp-ink-2)",
              maxWidth: 380,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            Pick the depth that suits you — both produce a five-step routine.
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            borderTop: "1px solid var(--bp-rule)",
            borderLeft: "1px solid var(--bp-rule)",
          }}
          className="bp-quiz-modes"
        >
          <ModeOption
            number="01"
            title="Quick"
            description="Four questions — a solid routine in under a minute."
            chips={["~30 SEC", "04 QUESTIONS"]}
            onClick={() => setMode("short")}
          />
          <ModeOption
            number="02"
            title="Extended"
            description="Seven questions — adds skin tone, climate and lifestyle for a fully personalised routine."
            chips={["~1 MIN", "07 QUESTIONS", "RECOMMENDED"]}
            onClick={() => setMode("extended")}
          />
        </div>
        <style>{`
          @media (max-width: 700px) {
            .bp-quiz-modes { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    );
  }

  // ─── LEAD CAPTURE GATE (after the routine is computed, before it's revealed) ───
  if (result && !leadDone) {
    return (
      <div style={{ maxWidth: 460, margin: "0 auto", textAlign: "center" }}>
        <span className="bp-mono" style={{ color: "var(--bp-ink-3)" }}>
          <span className="bp-bracket">{dict.quizUnlockKicker ?? "ALMOST THERE"}</span>
        </span>
        <h3
          style={{
            fontFamily: "var(--bp-serif)",
            fontWeight: 300,
            letterSpacing: "-0.025em",
            fontSize: "clamp(28px, 4vw, 48px)",
            lineHeight: 1.05,
            margin: "16px 0 0",
            textWrap: "balance" as never,
          }}
        >
          {dict.quizUnlockTitle ?? "Unlock your routine"}
        </h3>
        <p style={{ marginTop: 12, fontSize: 15, color: "var(--bp-ink-2)", lineHeight: 1.55 }}>
          {dict.quizUnlockSubtitle ??
            "Enter your details and we'll reveal your personalized five-step routine."}
        </p>
        <form
          onSubmit={submitLead}
          style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 16, textAlign: "left" }}
        >
          <LeadField label={dict.quizEmailLabel ?? "Email"}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={dict.quizEmailPlaceholder ?? "you@example.com"}
              disabled={leadSubmitting}
              style={leadInputStyle}
            />
          </LeadField>
          <LeadField label={dict.quizPhoneLabel ?? "Mobile"} optional={dict.quizOptional ?? "optional"}>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={dict.quizPhonePlaceholder ?? "+1 555 000 0000"}
              disabled={leadSubmitting}
              style={leadInputStyle}
            />
          </LeadField>
          <LeadField label={dict.quizInstagramLabel ?? "Instagram"} optional={dict.quizOptional ?? "optional"}>
            <input
              type="text"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder={dict.quizInstagramPlaceholder ?? "@yourhandle"}
              disabled={leadSubmitting}
              style={leadInputStyle}
            />
          </LeadField>
          {leadError && (
            <p className="bp-mono-sm" style={{ color: "#b00020" }}>
              {leadError}
            </p>
          )}
          <button type="submit" className="bp-btn" disabled={leadSubmitting} style={{ marginTop: 4 }}>
            {leadSubmitting
              ? dict.quizUnlockLoading ?? "REVEALING…"
              : dict.quizUnlockCta ?? "SEE MY ROUTINE →"}
          </button>
          <p className="bp-mono-sm" style={{ color: "var(--bp-ink-3)", lineHeight: 1.5 }}>
            {dict.quizPrivacy ??
              "We'll only use this to send your routine and skincare tips. Never shared or sold."}
          </p>
        </form>
      </div>
    );
  }

  // ─── RESULT SCREEN ───
  if (result) {
    // Distinct products to buy — AM and PM share items (cleanse, tone, hydrate),
    // so count unique SKUs rather than summing step counts.
    const totalProducts = new Set([...result.am, ...result.pm].map((p) => p.sku)).size;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 48 }}>
        {/* Profile header */}
        <div style={{ textAlign: "center" }}>
          <span className="bp-mono" style={{ color: "var(--bp-ink-3)" }}>
            <span className="bp-bracket">{dict.yourSkinProfile ?? "YOUR SKIN PROFILE"}</span>
          </span>
          <h3
            style={{
              fontFamily: "var(--bp-serif)",
              fontWeight: 300,
              letterSpacing: "-0.025em",
              fontSize: "clamp(36px, 5vw, 72px)",
              lineHeight: 1.02,
              margin: "16px 0 0",
              textWrap: "balance" as never,
            }}
          >
            {result.profile}
          </h3>
          <p
            style={{
              marginTop: 16,
              fontSize: 16,
              color: "var(--bp-ink-2)",
              maxWidth: 600,
              marginLeft: "auto",
              marginRight: "auto",
              lineHeight: 1.55,
            }}
          >
            {result.summary}
          </p>
        </div>

        {/* Profile signature tags — letter-badged like ingredients */}
        {result.colors.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {result.colors.map((c) => {
              const color = BP_COLORS[c];
              return (
                <span
                  key={c}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 14px",
                    border: "1px solid var(--bp-ink)",
                    background: "var(--bp-bg)",
                  }}
                >
                  <span className="bp-letter-badge">{color.label[0]}</span>
                  <span className="bp-mono-sm" style={{ color: "var(--bp-ink)" }}>
                    {color.label.toUpperCase()}
                  </span>
                </span>
              );
            })}
          </div>
        )}

        {/* Stats strip */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            borderTop: "1px solid var(--bp-rule)",
            borderBottom: "1px solid var(--bp-rule)",
          }}
        >
          {[
            { v: String(result.am.length).padStart(2, "0"), l: "AM steps" },
            { v: String(result.pm.length).padStart(2, "0"), l: "PM steps" },
            { v: String(totalProducts).padStart(2, "0"), l: "total products" },
          ].map((s, i) => (
            <div
              key={i}
              style={{
                padding: "24px 16px",
                textAlign: "center",
                borderRight: i < 2 ? "1px solid var(--bp-rule)" : "none",
              }}
            >
              <div
                style={{
                  fontFamily: "var(--bp-serif)",
                  fontWeight: 300,
                  letterSpacing: "-0.02em",
                  fontSize: "clamp(40px, 4.5vw, 64px)",
                  lineHeight: 1,
                }}
              >
                {s.v}
              </div>
              <div className="bp-mono-sm" style={{ marginTop: 8 }}>
                {s.l.toUpperCase()}
              </div>
            </div>
          ))}
        </div>

        {/* AM / PM routines */}
        <div
          className="bp-quiz-result-grid"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}
        >
          <RoutinePanel label="MORNING" steps={result.am} locale={locale} />
          <RoutinePanel label="EVENING" steps={result.pm} locale={locale} />
        </div>

        {/* Actions row */}
        <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={restart}
            className="bp-mono-sm"
            style={{
              padding: "12px 18px",
              background: "transparent",
              color: "var(--bp-ink)",
              border: "1px solid var(--bp-ink)",
              cursor: "pointer",
            }}
          >
            ↺ RETAKE
          </button>
          <button
            onClick={() => handleSaveRoutine(result)}
            className="bp-mono-sm"
            style={{
              padding: "12px 18px",
              background: saved ? "var(--bp-ink)" : "transparent",
              color: saved ? "var(--bp-bg)" : "var(--bp-ink)",
              border: "1px solid var(--bp-ink)",
              cursor: "pointer",
            }}
          >
            {saved ? "✓ SAVED" : "SAVE RESULT"}
          </button>
          <button
            onClick={() => handleShare(result)}
            className="bp-mono-sm"
            style={{
              padding: "12px 18px",
              background: shared ? "var(--bp-ink)" : "transparent",
              color: shared ? "var(--bp-bg)" : "var(--bp-ink)",
              border: "1px solid var(--bp-ink)",
              cursor: "pointer",
            }}
          >
            {shared ? "✓ COPIED" : "SHARE ↗"}
          </button>
          <Link
            href={`/${locale}/products`}
            className="bp-btn"
            style={{ padding: "12px 18px" }}
          >
            BROWSE THE RANGE →
          </Link>
        </div>

        <style>{`
          @media (max-width: 800px) {
            .bp-quiz-result-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    );
  }

  // ─── QUIZ SCREEN ───
  const selectedMulti = isMulti ? ((answers[q.id] || []) as string[]) : [];

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      {/* Progress strip */}
      <div style={{ marginBottom: 32 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <span className="bp-mono-sm">
            QUESTION {String(step + 1).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
          </span>
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              className="bp-mono-sm"
              style={{
                background: "none",
                border: 0,
                color: "var(--bp-ink)",
                cursor: "pointer",
                borderBottom: "1px solid var(--bp-ink)",
                paddingBottom: 1,
              }}
            >
              ← BACK
            </button>
          )}
        </div>
        <div
          style={{
            height: 1,
            background: "var(--bp-rule)",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: 1,
              width: `${progress}%`,
              background: "var(--bp-ink)",
              transition: "width 400ms cubic-bezier(.2,.7,.2,1)",
            }}
          />
        </div>
      </div>

      {/* Question */}
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <h3
          style={{
            fontFamily: "var(--bp-serif)",
            fontWeight: 300,
            letterSpacing: "-0.025em",
            fontSize: "clamp(28px, 3.6vw, 48px)",
            lineHeight: 1.1,
            margin: 0,
            textWrap: "balance" as never,
          }}
        >
          {q.question}
        </h3>
        <p
          style={{
            marginTop: 12,
            fontSize: 15,
            color: "var(--bp-ink-2)",
            maxWidth: 480,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {q.subtitle}
        </p>
      </div>

      {/* Options */}
      <div
        className="bp-quiz-options"
        style={{
          display: "grid",
          gridTemplateColumns: q.options.length <= 3 ? "1fr" : "1fr 1fr",
          borderTop: "1px solid var(--bp-rule)",
          borderLeft: "1px solid var(--bp-rule)",
        }}
      >
        {q.options.map((opt) => {
          const isSelected = isMulti
            ? selectedMulti.includes(opt.value)
            : answers[q.id] === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => selectOption(opt.value)}
              style={{
                textAlign: "left",
                padding: "20px 22px",
                display: "grid",
                gridTemplateColumns: "28px 1fr",
                gap: 16,
                alignItems: "start",
                background: isSelected ? "var(--bp-ink)" : "var(--bp-bg)",
                color: isSelected ? "var(--bp-bg)" : "var(--bp-ink)",
                borderTopWidth: 0,
                borderLeftWidth: 0,
                borderRightWidth: 1,
                borderBottomWidth: 1,
                borderRightStyle: "solid",
                borderBottomStyle: "solid",
                borderRightColor: "var(--bp-rule)",
                borderBottomColor: "var(--bp-rule)",
                cursor: "pointer",
                transition: "background 120ms",
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  border: `1px solid ${isSelected ? "var(--bp-bg)" : "var(--bp-ink)"}`,
                  background: isSelected ? "var(--bp-bg)" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 2,
                  flexShrink: 0,
                }}
              >
                {isSelected && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "var(--bp-ink)",
                    }}
                  />
                )}
              </span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "var(--bp-serif)",
                    fontSize: 20,
                    fontWeight: 300,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                  }}
                >
                  {opt.label}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: isSelected ? "oklch(0.85 0 0)" : "var(--bp-ink-3)",
                    marginTop: 6,
                    lineHeight: 1.45,
                  }}
                >
                  {opt.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Multi-select continue button */}
      {isMulti && (
        <div style={{ marginTop: 32, textAlign: "center" }}>
          <button
            onClick={nextMulti}
            disabled={selectedMulti.length === 0}
            className="bp-btn"
            style={{
              opacity: selectedMulti.length > 0 ? 1 : 0.4,
              cursor: selectedMulti.length > 0 ? "pointer" : "not-allowed",
            }}
          >
            CONTINUE WITH {String(selectedMulti.length).padStart(2, "0")} SELECTED →
          </button>
        </div>
      )}

      <style>{`
        @media (max-width: 700px) {
          .bp-quiz-options { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

/* ─── Helpers used by the redesigned views ─── */

function ModeOption({
  number,
  title,
  description,
  chips,
  onClick,
}: {
  number: string;
  title: string;
  description: string;
  chips: string[];
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: 28,
        background: 'var(--bp-bg)',
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderRightStyle: 'solid',
        borderBottomStyle: 'solid',
        borderRightColor: 'var(--bp-rule)',
        borderBottomColor: 'var(--bp-rule)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        minHeight: 220,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="bp-mono">{number}</span>
        <span className="bp-mono-sm" style={{ color: 'var(--bp-ink-3)' }}>QUIZ</span>
      </div>
      <h4
        style={{
          fontFamily: 'var(--bp-serif)',
          fontWeight: 300,
          letterSpacing: '-0.02em',
          fontSize: 36,
          margin: '12px 0 0',
          lineHeight: 1,
          color: 'var(--bp-ink)',
        }}
      >
        {title}
      </h4>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--bp-ink-2)', lineHeight: 1.55 }}>
        {description}
      </p>
      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
        }}
      >
        {chips.map((c) => (
          <span
            key={c}
            className="bp-mono-sm"
            style={{
              padding: '4px 8px',
              border: '1px solid var(--bp-rule)',
              color: 'var(--bp-ink-2)',
            }}
          >
            {c}
          </span>
        ))}
      </div>
    </button>
  );
}

function RoutinePanel({ label, steps, locale }: { label: string; steps: ProductRec[]; locale: string }) {
  return (
    <div style={{ background: 'var(--bp-bg)', border: '1px solid var(--bp-rule)' }}>
      <div
        style={{
          padding: '18px 20px',
          borderBottomWidth: 1,
          borderBottomStyle: 'solid',
          borderBottomColor: 'var(--bp-rule)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <span className="bp-mono-sm">{label} · ROUTINE</span>
        <span className="bp-mono-sm" style={{ color: 'var(--bp-ink-3)' }}>
          {String(steps.length).padStart(2, '0')} STEPS
        </span>
      </div>
      <div>
        {steps.map((p, i) => (
          <RoutineStepRow key={`${label}-${p.sku}-${i}`} product={p} index={i} locale={locale} />
        ))}
      </div>
    </div>
  );
}


const leadInputStyle: CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  border: "1px solid var(--bp-ink)",
  background: "var(--bp-bg)",
  color: "var(--bp-ink)",
  fontFamily: "inherit",
  fontSize: 15,
  outline: "none",
};

function LeadField({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: "block" }}>
      <span
        className="bp-mono-sm"
        style={{ display: "block", marginBottom: 6, color: "var(--bp-ink-2)" }}
      >
        {label.toUpperCase()}
        {optional ? (
          <span style={{ color: "var(--bp-ink-3)" }}> · {optional.toUpperCase()}</span>
        ) : null}
      </span>
      {children}
    </label>
  );
}
